import { capitalize } from 'lodash'
import {
  CreateParams,
  DataProvider,
  GetOneParams,
  UpdateParams,
} from 'react-admin'
import { groupBy } from 'resources/operations/events/utils'
import { FILTER_SOURCES } from 'services/cfg/filter'
import { translate } from 'services/i18n'
import {
  convertEmptyStringToUndefined,
  getById,
  getDefaultListParams,
  mapValues,
} from 'services/utils'
import { NamedService, SuppliedService, WithId } from 'types/common'
import { APIEntity } from '..'
import { APICfg } from '../../api'
import {
  callPost,
  getPath,
  multiPlexGetOneHandler,
  multiPlexUpdateHandler,
} from '../../dataProvider/common'
import { parseKDocks } from '../catalog/utils'
import { Food } from '../catalog/food/types/internal/food-models'
import { uploadOrKeepMultipleFiles } from '../common'
import kampaayDataProvider from './../../dataProvider'
import { mapEventToAPI, parseEventMultiplex } from './eventsMultiplexMap'
import {
  BaseProductToAPI,
  EventLocationToApi,
  EventLogisticToAPI,
  EventProductServiceToAPI,
  EventProfessionalServiceToAPI,
  EventToAPI,
  ProfessionalToAPI,
} from './types/api/request-models'
import {
  APIEvent,
  APIEventProfessionalService,
  APIFoodSnapshot,
  APILocationSnapshot,
  APILogisticSnapshot,
  APIProfessionalSnapshot,
} from './types/api/response-models'
import {
  EventItem,
  MappedEnrichedEvent,
  UnMappedEnrichedEvent,
} from './types/internal/event-models'
import {
  FoodSelection,
  LocationSelection,
  MainProductSelection,
  DrinkSelection,
  OptionGroupSelection,
  OptionedProductSelection,
  ProfessionalSelection,
  LogisticSelection,
  IntolerancesMap,
} from './types/internal/selections'
import {
  EventProfessionalService,
  EventProductService,
} from './types/internal/services'

const namedServicesArray: NamedService[] = [
  'foodService',
  'musicService',
  'rentService',
  'virtualService',
  'drinkService',
  'physicalService',
  'locationService',
]

const getProfessionalSelectionByType = (
  figureSelection: APIProfessionalSnapshot[],
  serviceSectionId: number
) =>
  figureSelection
    .filter(({ sectionId }) => sectionId === serviceSectionId)
    .map(({ bookedFrom, bookedTo, ...rest }) => ({
      ...rest,
      startTime: bookedFrom,
      endTime: bookedTo,
    }))

const mapProfessionalsToEvent =
  (proFigureSelection: APIProfessionalSnapshot[]) =>
  (
    professionalService: APIEventProfessionalService
  ): EventProfessionalService => {
    const { sectionId, sectionName, ...rest } = professionalService
    return {
      ...rest,
      id: sectionId,
      name: sectionName,
      supplier: professionalService.supplier?.id,
      supplierName: professionalService.supplier?.name,
      supplyConfirmed:
        professionalService.supplyConfirmed === 'confirmed' ? true : false,
      selections: getProfessionalSelectionByType(
        proFigureSelection,
        professionalService.sectionId
      ),
    }
  }

const mapFoodToEvent =
  (logistics: APILogisticSnapshot[]) =>
  (food: APIFoodSnapshot): FoodSelection => ({
    ...food,
    logisticSelection: getById(logistics, food.logisticSelection)!,
  })

const mapLocationToEvent = (
  location: APILocationSnapshot
): LocationSelection => ({
  ...location,
})

const mapBaseProductToAPI = ({
  id,
  quantity,
}: MainProductSelection): BaseProductToAPI => ({
  id: id!,
  quantity: quantity!,
})

const mapLocationToAPI = ({
  id,
  bookedFrom,
  bookedTo,
  quantity,
  arrangementName,
  upsellingSelection,
}: LocationSelection): EventLocationToApi => ({
  id: id!,
  bookedFrom: bookedFrom!,
  bookedTo: bookedTo!,
  quantity: quantity ?? 1,
  arrangementName: convertEmptyStringToUndefined(arrangementName),
  upselling: upsellingSelection.map(({ id, quantity }) => ({
    id: id!,
    quantity: quantity!,
  })),
})

const mapServiceToApi = (s: EventProductService): EventProductServiceToAPI => ({
  ...s,
  supplier: convertEmptyStringToUndefined(s.supplier),
  lastMessage: convertEmptyStringToUndefined(s.lastMessage),
  customization: !!s.customization?.name ? s.customization : undefined,
})

const mapDrinkToAPI = (drink: DrinkSelection) => {
  return {
    ...mapBaseProductToAPI(drink),
    quantity: (drink.variants ?? []).sum((e) => e.quantity),
    variants: drink.variants?.map(mapBaseProductToAPI) ?? [],
  }
}

const getOptionsDictionary = (options: OptionGroupSelection[]) =>
  options.reduce(
    (acc, opt) => ({
      ...acc,
      [opt.id!]: opt.items.map((i) => ({ id: i.id, quantity: i.quantity })),
    }),
    Object.create(null)
  )

const mapOptionedToAPI =
  (isVirtual = false) =>
  (optioned: OptionedProductSelection) => {
    const { upsellingSelection, optionSelections } = optioned
    return {
      id: optioned.id!,
      quantity: optioned.quantity!,
      // apparently upsellingSelection may be undefined if upselling not inserted in the form we should probably handle this in the interface
      upselling:
        upsellingSelection?.map(({ id, quantity }) => ({
          id: id!,
          quantity: isVirtual ? optioned.quantity! : quantity!,
        })) ?? [],
      options: getOptionsDictionary(optionSelections ?? []),
    }
  }

const getFoodLogisticSelection = (
  food: FoodSelection,
  logistics: EventLogisticToAPI[]
) => {
  return getById(logistics as WithId[], food.logisticSelection!.id)
    ? food.logisticSelection!.id
    : logistics.find((l) => l.deliveryTime === food.deliveryTime)!.id
}

const mapIntolerancesToAPI = (intolerances: IntolerancesMap) =>
  Object.entries(intolerances).reduce(
    (acc, [key, value]) => ({
      ...acc,
      // If value is 0, it will be converted to undefined for schema consistency
      [key]: !!value ? value : undefined,
    }),
    {}
  )

const mapFoodToAPI =
  (logistics: EventLogisticToAPI[]) => (food: FoodSelection) => {
    const { logisticSelection, intolerances, deliveryTime, ...rest } = food

    return {
      ...mapOptionedToAPI()(rest),
      intolerances: mapIntolerancesToAPI(intolerances),
      deliveryTime: deliveryTime!,
      logisticSelection: getFoodLogisticSelection(food, logistics)!,
    }
  }

const mapProSelectionToAPI = (
  selection: ProfessionalSelection
): ProfessionalToAPI => {
  const { id, quantity, startTime, endTime } = selection
  return {
    id: id!,
    quantity: quantity!,
    bookedFrom: startTime!,
    bookedTo: endTime!,
  }
}

const mapProServicesToApi = (
  proService: EventProfessionalService
): EventProfessionalServiceToAPI => {
  const { id, notes, triggerAssignmentEmail, supplier, lastMessage } =
    proService
  return {
    section: id,
    ...mapServiceToApi(proService),
    notes,
    triggerAssignmentEmail,
    supplier: convertEmptyStringToUndefined(supplier),
    lastMessage,
  }
}

const getEventLogisticPrice = (eventLogistic: LogisticSelection) =>
  'price' in eventLogistic
    ? eventLogistic.price
    : // in the case of logistics we assume that they only have one price range with only fixedPrice, since they always have quantity === 1
      eventLogistic.pricing[0].fixedPrice ?? 0

const mapSingleLogistic = ({
  deliveryTime,
  logisticSelection,
}: FoodSelection) => ({
  id: logisticSelection!.id,
  deliveryTime: deliveryTime!,
  price: getEventLogisticPrice(logisticSelection!),
})

const mapLogisticsToAPI = (
  catalogFoods: Food[],
  foodSelection: FoodSelection[]
): EventLogisticToAPI[] => {
  const logistics: EventLogisticToAPI[] = foodSelection.map(mapSingleLogistic)

  const groupedByDelivery = groupBy(logistics, (l) => l.deliveryTime)
  const essentialLogistics = Object.values(groupedByDelivery).flatMap(
    (group) =>
      group.sort(({ price: price1 }, { price: price2 }) => price2 - price1)[0]
  )

  // here we check that all foods share the essentials logistics we computed.
  // if some foods share the deliverytime and do not have the resulting logistic,
  // we add back in the cart the selected logistic for the food
  for (const food of foodSelection) {
    if (
      essentialLogistics.some(
        (l) =>
          l.deliveryTime === food.deliveryTime &&
          !(getById(catalogFoods, food.id)?.logistics ?? []).includes(l.id!)
      )
    ) {
      essentialLogistics.push(mapSingleLogistic(food))
    }
  }

  return essentialLogistics
}

const writeEventDatesToAPI = ({
  startDate,
  endDate,
  rentPickupDate,
  musicPickupDate,
}: EventItem) => ({
  startDate: startDate.toISOString(),
  endDate: endDate.toISOString(),
  rentPickupDate: rentPickupDate?.toISOString(),
  musicPickupDate: musicPickupDate?.toISOString(),
})

const parseEventDates = ({
  startDate,
  endDate,
  rentPickupDate,
  musicPickupDate,
}: APIEvent) => ({
  startDate: new Date(startDate),
  endDate: new Date(endDate),
  rentPickupDate: rentPickupDate ? new Date(rentPickupDate) : undefined,
  musicPickupDate: musicPickupDate ? new Date(musicPickupDate) : undefined,
})

const SEPARATOR = ', '

const getEventName = (id: number) => `${translate('common.label.event')} #${id}`

export const eventParse = (res: APIEvent): EventItem => {
  const { foodService, rentService, musicService, kampaayerDocs, ...rest } = res

  const productServices = namedServicesArray.reduce<
    Record<NamedService, EventProductService>
  >((acc, serv) => {
    const service = res[serv as SuppliedService]

    return {
      ...acc,
      [serv]: {
        customization: service?.customization,
        supplyConfirmed:
          service?.supplyConfirmed === 'confirmed' ? true : false,
        supplier: service?.supplier?.id,
        supplierName: service?.supplier?.name,
        lastMessage: service?.lastMessage,
        lastMessageConfirmed: service?.lastMessageConfirmed,
      },
    }
  }, Object.create(null))

  const foods = rest.foods?.map(mapFoodToEvent(rest.logistics)) ?? []
  const locations = rest.locations?.map(mapLocationToEvent) ?? []

  const purchasedServices = rest.purchasedServices
    ?.map(capitalize)
    .join(SEPARATOR)

  const response: EventItem = {
    ...rest,
    ...parseEventDates(res),
    name: res.name ?? getEventName(rest.id),
    customer: rest.customer.id,
    customerInfo: rest.customer,
    purchasedServices,
    customerDocs: parseKDocks(res.customerDocs),
    kampaayerDocs: parseKDocks(kampaayerDocs),
    purchasedServicesArray: rest.purchasedServices,
    professionals:
      rest.professionalServices?.map(
        mapProfessionalsToEvent(rest.professionals)
      ) ?? [],
    professionalStatus: rest.professionalStatus,
    productServices,
    virtuals: res.virtualExperiences ?? [],
    physicals: res.physicalExperiences ?? [],
    foods,
    locations,
  }
  return response
}

export const eventWrite = async (
  req: EventItem,
  dp: DataProvider
): Promise<EventToAPI> => {
  const {
    productServices,
    virtuals,
    physicals,
    locations,
    briefStatus,
    supplierStatus,
    fullAddress,
    rentDeliveryTime,
    musicDeliveryTime,
    ...rest
  } = req

  const {
    foodService,
    musicService,
    rentService,
    physicalService,
    locationService,
    drinkService,
    virtualService,
  } = mapValues(mapServiceToApi, productServices)

  const catalogFoods = await dp
    .getList<Food>('foods', getDefaultListParams())
    .then((res) => res?.data)

  const professionals =
    rest.professionals?.flatMap(({ selections }) =>
      selections.map((professional) => mapProSelectionToAPI(professional))
    ) ?? []

  const professionalServices =
    rest.professionals?.map(mapProServicesToApi) ?? []

  const kampaayerDocs = await uploadOrKeepMultipleFiles(
    rest.kampaayerDocs ?? []
  )

  const mappedLogistics = mapLogisticsToAPI(catalogFoods, rest?.foods ?? [])
  return {
    ...rest,
    ...writeEventDatesToAPI(req),
    fullAddress: convertEmptyStringToUndefined(fullAddress),
    kampaayerDocs,
    musics: rest.musics?.map((product) => mapBaseProductToAPI(product)) ?? [],
    rents: rest.rents?.map((product) => mapBaseProductToAPI(product)) ?? [],
    drinks: rest.drinks?.map(mapDrinkToAPI) ?? [],
    foods: rest.foods?.map(mapFoodToAPI(mappedLogistics)) ?? [],
    logistics: mappedLogistics,
    virtualService,
    virtualExperiences: virtuals?.map(mapOptionedToAPI(true)) ?? [],
    physicalExperiences: physicals?.map(mapOptionedToAPI()) ?? [],
    couponCode: rest.coupon?.code,
    professionals,
    professionalServices,
    foodService,
    rentService,
    musicService,
    physicalService,
    locations: locations?.map(mapLocationToAPI) ?? [],
    locationService,
    drinkService,
  }
}

export const eventGetOne = async (resource: string, params: GetOneParams) => {
  const [eventResponse, serviceConfigResp, eventBriefResp] =
    await multiPlexGetOneHandler(
      params,
      resource as APIEntity,
      'servicesconfig',
      'eventbrief'
    )

  const multiResponse: { data: UnMappedEnrichedEvent } = {
    data: {
      ...eventResponse!.data,
      serviceConfig: serviceConfigResp!.data,
      surveyResponses: eventBriefResp!.data,
    },
  }

  return { data: parseEventMultiplex(multiResponse.data) }
}

export const eventUpdate = (
  resource: string,
  { data }: UpdateParams<MappedEnrichedEvent>
) => {
  const { surveyResponses, ...event } = mapEventToAPI(
    data as MappedEnrichedEvent
  )
  const eventId = event.id

  return multiPlexUpdateHandler(
    {
      resource: resource as APIEntity,
      params: { id: eventId, data: event },
    },
    {
      resource: 'eventbrief',
      params: {
        data: surveyResponses,
        id: eventId,
      },
    }
  )
}

const eventCreate = async (
  _resource: string,
  params: CreateParams<MappedEnrichedEvent>
) => {
  const { surveyResponses, ...event } = mapEventToAPI(
    params.data as MappedEnrichedEvent
  )
  const mappedEvent = await eventWrite(event, kampaayDataProvider)
  return callPost(apiCfg, getPath(apiCfg, params), 'POST', mappedEvent)
}

const apiCfg = {
  endpoint: 'event',
  query: { [FILTER_SOURCES.status]: 'confirmed' },
  parse: eventParse,
  write: eventWrite,
  getOne: eventGetOne,
  update: eventUpdate,
  create: eventCreate,
} as APICfg

export default apiCfg
